import { useState } from 'react'
import {CheckCircle, AlertCircle, TrendingUp, Zap} from 'lucide-react'
import CalculatorResults from '@/components/CalculatorResults'

interface FormData {
  age: string
  gender: string
  lifestyle: string
  energyLevel: number
  concerns: string[]
  vitaminDLevel: string
}

function Calculator() {
  const [step, setStep] = useState<'form' | 'results'>('form')
  const [formData, setFormData] = useState<FormData>({
    age: '',
    gender: '',
    lifestyle: '',
    energyLevel: 5,
    concerns: [],
    vitaminDLevel: ''
  })

  const handleConcernToggle = (concern: string) => {
    setFormData(prev => ({
      ...prev,
      concerns: prev.concerns.includes(concern)
        ? prev.concerns.filter(c => c !== concern)
        : [...prev.concerns, concern]
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setStep('results')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleReset = () => {
    setFormData({
      age: '',
      gender: '',
      lifestyle: '',
      energyLevel: 5,
      concerns: [],
      vitaminDLevel: ''
    })
    setStep('form')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  if (step === 'results') {
    return <CalculatorResults formData={formData} onReset={handleReset} />
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-orange-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-100 rounded-full mb-4">
            <TrendingUp className="w-8 h-8 text-teal-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Vitamin Vital<span className="text-teal-600"> Calculator</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Answer a few questions to receive your personalized Vitality Score and supplement recommendations
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          {/* Age & Gender */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="inline-flex items-center justify-center w-8 h-8 bg-teal-500 text-white rounded-full text-sm font-bold mr-3">1</span>
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Age
                </label>
                <input
                  type="number"
                  required
                  min="18"
                  max="100"
                  value={formData.age}
                  onChange={(e) => setFormData({...formData, age: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Enter your age"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gender
                </label>
                <select
                  required
                  value={formData.gender}
                  onChange={(e) => setFormData({...formData, gender: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>

          {/* Lifestyle */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="inline-flex items-center justify-center w-8 h-8 bg-teal-500 text-white rounded-full text-sm font-bold mr-3">2</span>
              Lifestyle & Activity
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { value: 'sedentary', label: 'Sedentary', desc: 'Minimal activity' },
                { value: 'active', label: 'Active', desc: 'Regular exercise' },
                { value: 'athlete', label: 'Athlete', desc: 'High intensity' }
              ].map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setFormData({...formData, lifestyle: option.value})}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    formData.lifestyle === option.value
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-semibold text-gray-900 mb-1">{option.label}</div>
                  <div className="text-sm text-gray-600">{option.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Energy Level */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="inline-flex items-center justify-center w-8 h-8 bg-teal-500 text-white rounded-full text-sm font-bold mr-3">3</span>
              Current Energy Level
            </h3>
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">How energetic do you feel daily?</span>
                <div className="flex items-center space-x-2">
                  <Zap className="w-5 h-5 text-orange-500" />
                  <span className="text-2xl font-bold text-gray-900">{formData.energyLevel}/10</span>
                </div>
              </div>
              <input
                type="range"
                min="1"
                max="10"
                value={formData.energyLevel}
                onChange={(e) => setFormData({...formData, energyLevel: parseInt(e.target.value)})}
                className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-500"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-2">
                <span>Very Low</span>
                <span>Moderate</span>
                <span>Excellent</span>
              </div>
            </div>
          </div>

          {/* Health Concerns */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="inline-flex items-center justify-center w-8 h-8 bg-teal-500 text-white rounded-full text-sm font-bold mr-3">4</span>
              Specific Health Concerns
            </h3>
            <p className="text-sm text-gray-600 mb-4">Select all that apply:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { value: 'fatigue', label: 'Fatigue', icon: '😴' },
                { value: 'immunity', label: 'Weak Immunity', icon: '🛡️' },
                { value: 'hair', label: 'Hair Health', icon: '💇' },
                { value: 'brain', label: 'Brain Fog', icon: '🧠' }
              ].map(concern => (
                <button
                  key={concern.value}
                  type="button"
                  onClick={() => handleConcernToggle(concern.value)}
                  className={`p-4 rounded-lg border-2 transition-all flex items-center space-x-3 ${
                    formData.concerns.includes(concern.value)
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-2xl">{concern.icon}</span>
                  <span className="font-medium text-gray-900">{concern.label}</span>
                  {formData.concerns.includes(concern.value) && (
                    <CheckCircle className="w-5 h-5 text-teal-600 ml-auto" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Vitamin D Level (Optional) */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="inline-flex items-center justify-center w-8 h-8 bg-teal-500 text-white rounded-full text-sm font-bold mr-3">5</span>
              Vitamin D Level <span className="text-sm font-normal text-gray-500 ml-2">(Optional)</span>
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Vitamin D blood level (ng/mL)
              </label>
              <input
                type="number"
                min="0"
                max="150"
                value={formData.vitaminDLevel}
                onChange={(e) => setFormData({...formData, vitaminDLevel: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="e.g., 25 (Leave blank if unknown)"
              />
              <p className="text-xs text-gray-500 mt-2">
                Normal range: 30-50 ng/mL | Optimal: 40-60 ng/mL
              </p>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6 flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-700">
              <strong className="font-semibold">Disclaimer:</strong> This calculator is for informational purposes and is not medical advice. Consult a doctor before starting new supplements.
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!formData.age || !formData.gender || !formData.lifestyle}
            className="w-full py-4 bg-orange-500 text-white rounded-lg text-lg font-semibold hover:bg-orange-600 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Calculate My Vitality Score
          </button>
        </form>
      </div>
    </div>
  )
}

export default Calculator
